import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';

const SecurityTab = ({ formData, onChange, onSave, isSaving, onEnable2FA, onDisable2FA }) => {
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
          Beveiligingsinstellingen
        </h3>
        <p className="text-sm md:text-base text-muted-foreground mb-6">
          Beheer uw wachtwoord en beveiligingsopties
        </p>
      </div>
      <div className="border border-border rounded-lg p-4 md:p-6">
        <h4 className="text-lg md:text-xl font-medium text-foreground mb-4">
          Wachtwoord wijzigen
        </h4>

        <div className="space-y-4">
          <div className="relative">
            <Input
              label="Huidig wachtwoord"
              type={showCurrentPassword ? 'text' : 'password'}
              placeholder="Voer huidig wachtwoord in"
              value={formData?.currentPassword}
              onChange={(e) => onChange('currentPassword', e?.target?.value)}
              required
            />
            <button
              type="button"
              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-smooth"
              aria-label={showCurrentPassword ? 'Verberg wachtwoord' : 'Toon wachtwoord'}
            >
              <Icon name={showCurrentPassword ? 'EyeOff' : 'Eye'} size={20} />
            </button>
          </div>

          <div className="relative">
            <Input
              label="Nieuw wachtwoord"
              type={showNewPassword ? 'text' : 'password'}
              placeholder="Voer nieuw wachtwoord in"
              value={formData?.newPassword}
              onChange={(e) => onChange('newPassword', e?.target?.value)}
              required
              description="Minimaal 8 tekens, inclusief hoofdletter en cijfer"
            />
            <button
              type="button"
              onClick={() => setShowNewPassword(!showNewPassword)}
              className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-smooth"
              aria-label={showNewPassword ? 'Verberg wachtwoord' : 'Toon wachtwoord'}
            >
              <Icon name={showNewPassword ? 'EyeOff' : 'Eye'} size={20} />
            </button>
          </div>

          <div className="relative">
            <Input
              label="Bevestig nieuw wachtwoord"
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Bevestig nieuw wachtwoord"
              value={formData?.confirmPassword}
              onChange={(e) => onChange('confirmPassword', e?.target?.value)}
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-smooth"
              aria-label={showConfirmPassword ? 'Verberg wachtwoord' : 'Toon wachtwoord'}
            >
              <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={20} />
            </button>
          </div>

          <Button
            variant="default"
            onClick={onSave}
            loading={isSaving}
            iconName="Lock"
            iconPosition="left"
          >
            Wachtwoord bijwerken
          </Button>
        </div>
      </div>
      <div className="border border-border rounded-lg p-4 md:p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h4 className="text-lg md:text-xl font-medium text-foreground mb-2">
              Twee-factor authenticatie (2FA)
            </h4>
            <p className="text-sm text-muted-foreground">
              Voeg een extra beveiligingslaag toe aan uw account
            </p>
          </div>
          <div className={`px-3 py-1 rounded-full ${formData?.twoFactorEnabled ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'}`}>
            <span className="caption font-medium">
              {formData?.twoFactorEnabled ? 'Actief' : 'Inactief'}
            </span>
          </div>
        </div>

        {!formData?.twoFactorEnabled ? (
          <div className="space-y-4">
            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-foreground mb-2">
                    Twee-factor authenticatie verhoogt de beveiliging van uw account aanzienlijk.
                  </p>
                  <p className="caption text-muted-foreground">
                    U heeft een authenticator app nodig zoals Google Authenticator of Authy.
                  </p>
                </div>
              </div>
            </div>

            <Button
              variant="default"
              onClick={onEnable2FA}
              iconName="Shield"
              iconPosition="left"
            >
              2FA inschakelen
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-success/10 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-3">
                <Icon name="CheckCircle" size={20} className="text-success" />
                <p className="text-sm font-medium text-success">
                  Twee-factor authenticatie is actief
                </p>
              </div>
              <p className="caption text-muted-foreground">
                Laatste verificatie: {formData?.lastVerification}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                variant="outline"
                iconName="RefreshCw"
                iconPosition="left"
              >
                Backup codes genereren
              </Button>
              <Button
                variant="destructive"
                onClick={onDisable2FA}
                iconName="ShieldOff"
                iconPosition="left"
              >
                2FA uitschakelen
              </Button>
            </div>
          </div>
        )}
      </div>
      <div className="border border-border rounded-lg p-4 md:p-6">
        <h4 className="text-lg md:text-xl font-medium text-foreground mb-4">
          Privacyinstellingen
        </h4>

        <div className="space-y-4">
          <Checkbox
            label="Profiel zichtbaar voor andere gebruikers"
            description="Andere gebruikers kunnen uw profiel bekijken"
            checked={formData?.profileVisible}
            onChange={(e) => onChange('profileVisible', e?.target?.checked)}
          />

          <Checkbox
            label="E-mailmeldingen ontvangen"
            description="Ontvang updates over opdrachten en berichten"
            checked={formData?.emailNotifications}
            onChange={(e) => onChange('emailNotifications', e?.target?.checked)}
          />

          <Checkbox
            label="Gegevens delen met affiliates"
            description="Sta affiliates toe om uw activiteit te volgen"
            checked={formData?.shareWithAffiliates}
            onChange={(e) => onChange('shareWithAffiliates', e?.target?.checked)}
          />

          <Checkbox
            label="Marketing communicatie"
            description="Ontvang nieuws en aanbiedingen"
            checked={formData?.marketingConsent}
            onChange={(e) => onChange('marketingConsent', e?.target?.checked)}
          />
        </div>
      </div>
      <div className="border border-error/20 rounded-lg p-4 md:p-6 bg-error/5">
        <h4 className="text-lg md:text-xl font-medium text-error mb-2">
          Gevarenzone
        </h4>
        <p className="text-sm text-muted-foreground mb-4">
          Deze acties zijn permanent en kunnen niet ongedaan worden gemaakt
        </p>

        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            variant="outline"
            iconName="Download"
            iconPosition="left"
          >
            Gegevens exporteren
          </Button>
          <Button
            variant="destructive"
            iconName="Trash2"
            iconPosition="left"
          >
            Account verwijderen
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SecurityTab;